import useListAllCreditInvoices, {
  ListAllCreditInvoicesRequest,
} from "@/features/invoices/credit-invoices/hooks/use-list-all-credit-invoices";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useMemo } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import AppSelect from "@/features/line-sheet-sets/app-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  CreditInvoice,
  formatCreditInvoiceLabel,
  UsedCreditInvoiceSummary,
} from "@/features/invoices/credit-invoices/credit-invoice.type";

type CreditInvoiceSelectValue = UsedCreditInvoiceSummary;

interface AppCreditInvoiceSelectorProps {
  request?: ListAllCreditInvoicesRequest;
  excludeIds: number[];
  onSelect: (value: CreditInvoiceSelectValue) => void;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  width?: string;
}

export default function AppCreditInvoiceSelector({
  request,
  excludeIds = [],
  onSelect,
  isDisabled = false,
  isReadOnly = false,
  width = "100%",
}: AppCreditInvoiceSelectorProps) {
  const identity = useIdentity();
  const { t, tTitle, tCurrencyString } = useI18nHelper();
  const { error: showError } = useAppToasts();

  const key = useMemo(() => {
    if (request && identity?.company) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
  }, [identity, request]);

  const {
    data: resources,
    isLoading: isListResourcesLoading,
    error: listResourcesError,
  } = useListAllCreditInvoices(key?.parameter, key?.request);

  const options = useMemo((): AppSelectOption<CreditInvoice>[] => {
    if (resources) {
      return resources
        .flatMap((resource) => resource)
        .filter((i) => !excludeIds.includes(i.id))
        .map((i) => {
          return {
            value: i,
            name: formatCreditInvoiceLabel(i, {
              t,
              formatAmount: tCurrencyString,
            }),
          };
        });
    }
    return [];
  }, [resources, excludeIds, t, tCurrencyString]);

  return (
    <AppSelect<CreditInvoice>
      isSearchable={true}
      name={tTitle("credit")}
      width={width}
      idFunc={(i) => i.id}
      options={options}
      onSelect={(selectedValue) => {
        if (selectedValue) {
          onSelect(selectedValue);
        }
      }}
      isDisabled={isDisabled || options.length === 0}
      isReadOnly={isReadOnly}
      isClearable={false}
    />
  );
}
